import React, { useState } from "react";
import { Col, Dropdown } from "react-bootstrap";
import { ChevronDown, MapPin } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { formatDate, handleStopPropagation } from "components/common/Utils";
import { showAlert } from "store/reducers/alertReducer";
import { deleteBranch, updateBranchStatus } from "store/actions/branchSlice";
import { useDispatch } from "react-redux";
import DeleteConfirmationModal from "components/common/ConfirmationDialog";
import LockIcon from "assets/images/icons/lock.svg";
import LockIcon2 from "assets/images/icons/lock2.png";
import ClosureDetailsTooltip from "pages/Bookings/Workspace/ClosureDetailsTooltip";

const BranchCard = ({ item, className = "", isLoading }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [selectedId, setSelectedId] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);

  const handleDelete = () => {
    const payload = {
      id: selectedId,
      status: "2", // 2 FOR DELETE
    };
    dispatch(deleteBranch(payload))
      .then((action) => {
        const { statusCode, success, message } = action?.payload;
        if (statusCode === 200 && success) {
          dispatch(showAlert({ text: message, type: "success" }));
          setShowModal(false);
          setSelectedId(null);
        } else {
          dispatch(showAlert({ text: message, type: "error" }));
        }
      })
      .catch((err) => {});
  };

  const handleDraft = (id, status) => {
    const updatedStatus = status === 1 ? 3 : 1;

    const payload = {
      id,
      status: updatedStatus, // 3 FOR DRAFT, 1 FOR PUBLISH
    };
    dispatch(updateBranchStatus(payload))
      .then((action) => {
        const { statusCode, success, message } = action?.payload;
        if (statusCode === 200 && success) {
          dispatch(showAlert({ text: message, type: "success" }));
        } else {
          dispatch(showAlert({ text: message, type: "error" }));
        }
      })
      .catch((err) => {});
  };

  return (
    <>
      <Col xxl={3} lg={4}>
        <div
          className={`top_card_inner_section c_p ${className}`}
          onClick={() =>
            navigate(`/management/branches/branch-details?id=${item?._id}`)
          }
        >
          <div className="image_section position-relative">
            <img
              src={item?.imageUrls[0] || ""}
              alt={""}
              width={374}
              height={169}
            />
          </div>
          <div className="content">
            <div
              className="g_between align-items-start gap-3"
              onClick={handleStopPropagation}
            >
              <h2>{item?.name || "-"}</h2>

              {item?.closureDetails && (
                <div
                  onMouseEnter={() => setShowTooltip(true)}
                  onMouseLeave={() => setShowTooltip(false)}
                  style={{ position: "relative", display: "inline-block" }}
                >
                  <img src={LockIcon} alt="lock" width={18} height={19} />

                  {showTooltip && (
                    <ClosureDetailsTooltip
                      item={item}
                      setShowTooltip={setShowTooltip}
                    />
                  )}
                </div>
              )}
            </div>
            <div className="d-flex gap-4">
              <h3 className="in_line_one">
                <MapPin className="text_gray me-1" width={14} height={14} />
                {item?.cityId?.name || "-"}
              </h3>
            </div>
            <div className="g_between bottom_div">
              <p>
                <span className="">{t("Last Edit on")}</span>:{" "}
                {formatDate(item?.updatedAt)} by{" "}
                <span>{item?.lastEditedBy?.name || "-"}</span>
              </p>
              <Dropdown
                className="custom_dropdown list_action_dropdown"
                drop={"down"}
                onClick={handleStopPropagation}
              >
                <Dropdown.Toggle>
                  <button className="list_edit_btn">
                    {t("Edit")} <ChevronDown width={18} height={18} />
                  </button>
                </Dropdown.Toggle>

                <Dropdown.Menu align="end">
                  <Dropdown.Item
                    href="#"
                    onClick={() => handleDraft(item?._id, item.status)}
                  >
                    {item.status === 1 ? t("Draft") : t("Publish")}
                  </Dropdown.Item>
                  <Dropdown.Item
                    href="#"
                    onClick={() => {
                      navigate(
                        `/management/branches/branch-details?id=${item?._id}`
                      );
                    }}
                  >
                    {t("View")}
                  </Dropdown.Item>
                  <Dropdown.Item
                    href="#"
                    onClick={() => {
                      navigate(
                        `/management/branches/update-branch?id=${item._id}`
                      );
                    }}
                  >
                    {t("Edit")}
                  </Dropdown.Item>
                  <Dropdown.Item
                    href="#"
                    className="text_danger"
                    onClick={() => {
                      setSelectedId(item._id);
                      setShowModal(true);
                    }}
                  >
                    {t("Delete")}
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
            </div>
          </div>
        </div>
      </Col>
      <DeleteConfirmationModal
        show={showModal}
        onClose={() => {
          setShowModal(false);
          setSelectedId(null);
        }}
        onConfirm={handleDelete}
        message={t("Are you sure you want to delete this {{name}}?", {
          name: "Branch",
        })}
        isLoading={isLoading}
      />
    </>
  );
};

export default BranchCard;
